import React, { useEffect, useState, useCallback } from 'react';
import { 
  Tabs, 
  Tab, 
  Box, 
  Typography, 
  Button, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Switch, 
  FormControlLabel,
  Card,
  CardContent,
  Paper,
  Chip,
  InputAdornment,
  useTheme,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAllTaxonomyCodes, addTaxonomyCode, updateTaxonomyCode, deactivateTaxonomyCode } from '../../services/taxonomyCodesService';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  minHeight: 48,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.success.light : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 500,
  fontSize: '0.75rem',
}));

const codeTypes = [
  { key: 'provider', label: 'Provider Taxonomy' },
  { key: 'facility', label: 'Facility Taxonomy' },
  { key: 'specialty', label: 'Specialty Taxonomy' },
];

const defaultForm = {
  taxonomy_code: '',
  taxonomy_type: 'Provider Taxonomy',
  name: '',
  description: '',
  classification: '',
  specialization: '',
  active: true,
  notes: '',
};

const TaxonomyCodesPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    hasMore: true,
    nextCursor: null,
    total: null
  });
  const [currentLimit] = useState(50); // Use reasonable limit for better UX

  const fetchCodes = useCallback(async (startAfter = null, append = false) => {
    setLoading(true);
    try {
      const result = await getAllTaxonomyCodes({}, currentLimit, startAfter);
      
      if (result && result.taxonomyCodes) {
        if (append && startAfter) {
          // Append to existing codes for pagination
          setCodes(prevCodes => [...prevCodes, ...result.taxonomyCodes]);
        } else {
          // First load or refresh
          setCodes(result.taxonomyCodes);
        }
        
        // Update pagination state
        setPagination({
          hasMore: result.pagination?.hasMore || false,
          nextCursor: result.pagination?.nextCursor || null,
          total: result.pagination?.total || null
        });
      } else {
        console.warn('Unexpected result format:', result);
        setCodes([]);
        setPagination({ hasMore: false, nextCursor: null, total: null });
      }
    } catch (error) {
      console.error('Error fetching taxonomy codes:', error);
      setCodes([]);
      setPagination({ hasMore: false, nextCursor: null, total: null });
    } finally {
      setLoading(false);
    }
  }, [currentLimit]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleOpenDialog = (code) => {
    if (code) {
      setForm(code);
      setEditId(code.id);
    } else {
      setForm(defaultForm);
      setEditId(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm(defaultForm);
    setEditId(null);
  };

  const handleSave = async () => {
    if (editId) {
      await updateTaxonomyCode(editId, form);
    } else {
      await addTaxonomyCode(form);
    }
    handleCloseDialog();
    fetchCodes();
  };

  const handleDeactivate = async (id) => {
    await deactivateTaxonomyCode(id);
    fetchCodes();
  };

  const filteredCodes = codes.filter(
    c => c.taxonomy_type === codeTypes[tab].label &&
      (
        c.taxonomy_code?.toLowerCase().includes(search.toLowerCase()) ||
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.classification?.toLowerCase().includes(search.toLowerCase())
      )
  );

  // Load more functionality
  const handleLoadMore = () => {
    if (pagination.hasMore && pagination.nextCursor) {
      fetchCodes(pagination.nextCursor, true);
    }
  };

  // Reset codes when search or tab changes
  useEffect(() => {
    setCodes([]);
    setPagination({ hasMore: true, nextCursor: null, total: null });
    fetchCodes();
  }, [search, tab, fetchCodes]);

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Taxonomy Codes Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage healthcare provider taxonomy codes and classifications
              </Typography>
            </Box>
            <Button 
              variant="contained"
              onClick={() => navigate('/admin/codes')}
              startIcon={<ArrowBack />}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back to Codes
            </Button>
          </Box>
        </CardContent>
      </HeaderCard>

      <Box sx={{ mb: 3 }}>
        <StyledTabs value={tab} onChange={(_, v) => setTab(v)}>
          {codeTypes.map((ct) => (
            <StyledTab key={ct.key} label={ct.label} />
          ))}
        </StyledTabs>
      </Box>

      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <TextField
              label="Search codes"
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              variant="contained" 
              onClick={() => handleOpenDialog()}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Add New Code
            </Button>
          </Box>

          {!loading && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredCodes.length} {codeTypes[tab].label.toLowerCase()} codes
                {search && ` matching "${search}"`}
                {pagination.hasMore && ` (${pagination.nextCursor ? 'Load more available' : 'Initial load'})`}
              </Typography>
            </Box>
          )}

          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Code</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Classification</StyledTableCell>
                  <StyledTableCell>Specialization</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Loading taxonomy codes...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {search ? 'No taxonomy codes found matching your search.' : 'No taxonomy codes available for this type.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCodes.map(code => (
                    <TableRow 
                      key={code.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover 
                        } 
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{code.taxonomy_code}</TableCell>
                      <TableCell>{code.name}</TableCell>
                      <TableCell>{code.classification}</TableCell>
                      <TableCell>{code.specialization}</TableCell>
                      <TableCell>
                        <StatusChip 
                          active={code.active}
                          label={code.active ? 'Active' : 'Inactive'}
                          icon={code.active ? <CheckCircleIcon /> : <CancelIcon />}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <ActionButton
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenDialog(code)}
                            startIcon={<EditIcon />}
                          >
                            Edit
                          </ActionButton>
                          <ActionButton
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeactivate(code.id)}
                            startIcon={<DeleteIcon />}
                          >
                            {code.active ? 'Deactivate' : 'Activate'}
                          </ActionButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
          
          {pagination.hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading || !pagination.nextCursor}
                startIcon={loading ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                sx={{ borderRadius: '12px', textTransform: 'none' }}
              >
                {loading ? 'Loading...' : 'Load More Codes'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? 'Edit Taxonomy Code' : 'Add New Taxonomy Code'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Taxonomy Code"
              value={form.taxonomy_code}
              onChange={(e) => setForm({ ...form, taxonomy_code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Classification"
              value={form.classification}
              onChange={(e) => setForm({ ...form, classification: e.target.value })}
              fullWidth
            />
            <TextField
              label="Specialization"
              value={form.specialization}
              onChange={(e) => setForm({ ...form, specialization: e.target.value })}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxonomyCodesPage; 